import { useEffect, useState, useContext, createContext, useCallback, useMemo, useRef } from 'react';
import { TOPICS } from './Subscription';
import {
    ONLINE_WINDOW_MS,
    PRESENCE_TICK_MS,
    isOnlineAt,
    parsePresenceTime,
} from './presenceClock';

const OnlineStatusContext = createContext({
    isOnline: () => false,
    lastActiveAt: () => null,
    seed: () => {},
    setServerTime: () => {},
    now: () => Date.now(),
    tick: 0,
});

const applyEntry = (next, participantId, lastActiveAt) => {
    const parsed = parsePresenceTime(lastActiveAt);
    if (!participantId || parsed == null) {
        return;
    }
    const previous = next.get(String(participantId));
    if (previous == null || parsed >= previous) {
        next.set(String(participantId), parsed);
    }
};

export const OnlineStatusProvider = ({ children }) => {
    const [lastActiveById, setLastActiveById] = useState(() => new Map());
    const [tick, setTick] = useState(0);
    const offsetMsRef = useRef(0);

    const now = useCallback(() => Date.now() + offsetMsRef.current, []);

    const setServerTime = useCallback((serverTime) => {
        const parsed = parsePresenceTime(serverTime);
        if (parsed == null) {
            return;
        }
        offsetMsRef.current = parsed - Date.now();
        setTick((value) => value + 1);
    }, []);

    const seed = useCallback((entries) => {
        if (!entries || !entries.length) {
            return;
        }
        setLastActiveById((prev) => {
            const next = new Map(prev);
            entries.forEach((entry) => {
                if (Array.isArray(entry)) {
                    applyEntry(next, entry[0], entry[1]);
                    return;
                }
                applyEntry(next, entry?.participantId, entry?.lastActiveAt);
            });
            return next;
        });
    }, []);

    const handlePresenceUpdate = useCallback((event) => {
        const detail = event.detail || {};
        const participantId = detail.participantId;
        if (!participantId) {
            return;
        }
        let lastActiveAt = detail.lastActiveAt;
        if (lastActiveAt == null && typeof detail.online === 'boolean') {
            lastActiveAt = detail.online ? now() : now() - ONLINE_WINDOW_MS - 1;
        }
        seed([[participantId, lastActiveAt]]);
    }, [now, seed]);

    useEffect(() => {
        window.addEventListener(TOPICS.PRESENCE, handlePresenceUpdate);
        return () => window.removeEventListener(TOPICS.PRESENCE, handlePresenceUpdate);
    }, [handlePresenceUpdate]);

    useEffect(() => {
        const id = setInterval(() => setTick((value) => value + 1), PRESENCE_TICK_MS);
        return () => clearInterval(id);
    }, []);

    const lastActiveAt = useCallback((id) => {
        if (id == null) {
            return null;
        }
        return lastActiveById.get(String(id)) ?? null;
    }, [lastActiveById]);

    const isOnline = useCallback((id) => {
        return isOnlineAt(lastActiveAt(id), now());
    }, [lastActiveAt, now, tick]);

    const value = useMemo(() => ({
        isOnline,
        lastActiveAt,
        seed,
        setServerTime,
        now,
        tick,
    }), [isOnline, lastActiveAt, seed, setServerTime, now, tick]);

    return (
        <OnlineStatusContext.Provider value={value}>
            {children}
        </OnlineStatusContext.Provider>
    );
};

export const useOnlineStatus = () => useContext(OnlineStatusContext);
